import { ref, shallowRef, onUnmounted, computed } from 'vue'
import { fromLonLat } from 'ol/proj'
import OlMap from 'ol/Map'
import View from 'ol/View'
import GeoTIFF from 'ol/source/GeoTIFF.js'
import WebGlTileLayer from 'ol/layer/WebGLTile'
import Google from 'ol/source/Google'
import googleMapLogo from '/@/assets/Google_Maps_Attribution_Assets/GoogleMaps_Logo_Gray/GoogleMaps_Logo_Gray.svg'
import { defaults as defaultControls } from 'ol/control/defaults.js'
import Control from 'ol/control/Control'
import XYZ from 'ol/source/XYZ'
import { AMapConfig } from '/@/constants/index'
import { Style, Fill, Stroke, Text } from 'ol/style'
import type { Geometry } from 'ol/geom'
import { FeatureLike } from 'ol/Feature'

const map = shallowRef<OlMap | null>(null)
const isInitialized = ref(false)

// ---------- Loading state ----------
const tilesLoading = ref(0)
const tilesLoaded = ref(0)
const isLoading = computed(() => tilesLoading.value > 0)

const loadingProgress = computed(() => {
  const total = tilesLoading.value + tilesLoaded.value
  if (total === 0) return 100
  return Math.round((tilesLoaded.value / total) * 100)
})

export function useSharedMap () {
  function initMap (options?: ConstructorParameters<typeof Map>[0]) {
    if (map.value) return map.value

    const googleMapKey = AMapConfig.gmapKey
    const googleMapLayer =
        new WebGlTileLayer({
          id: 'googleMapLayer',
          source: new Google({
            key: googleMapKey,
            mapType: 'satellite', // 'satellite' | 'roadmap' | 'hybrid' | 'terrain'
            highDpi: true, // Better quality on retina screens
            scale: 'scaleFactor2x', // Optional extra scaling
            region: 'KR',
            attributionsCollapsible: true,
          }),
          visible: true,
        })
    class GoogleLogoControl extends Control {
      constructor () {
        const element = document.createElement('img')
        element.style.pointerEvents = 'none'
        element.style.position = 'absolute'
        element.style.bottom = '5px'
        element.style.left = '5px'
        element.style.display = 'block'
        element.style.opacity = '1'
        element.src = googleMapLogo
        super({
          element: element,
        })
        this.name = 'GoogleLogoControl'
        this.element = element
      }

      show () {
        this.element.style.display = 'block'
        this.element.style.opacity = '1'
      }

      hide () {
        this.element.style.opacity = '0'
        setTimeout(() => {
          this.element.style.display = 'none'
        }, 300)
      }
    }

    const projectPath = AMapConfig.url + AMapConfig.path
    const baseUrl = projectPath + '/orthomosaic_cog_3857.tif'
    const tilesPath = projectPath + '/tiles/{z}/{x}/{y}.png'

    const sharedView = new View({
      center: fromLonLat([parseFloat(AMapConfig.longitude), parseFloat(AMapConfig.latitude)]),
      zoom: 13,
      projection: 'EPSG:3857',
      maxZoom: 22 // limit how far users can zoom in
    })

    const baseXyzLayer =
        new WebGlTileLayer({
          id: 'baseXyzLayer',
          source: new XYZ({
            url: tilesPath,
            tileSize: 256,
            transition: 0,
          }),
          preload: 9, // Prefetch neighbor tiles for smooth panning
          maxZoom: 22,
          minZoom: 18,
        })

    const baseCogLayer =
        new WebGlTileLayer({
          id: 'baseCogLayer',
          source: new GeoTIFF({
            sources: [
              {
                url: baseUrl,
              }
            ],
            transition: 0,
            // projection: 'EPSG:3857'
          }),
          cacheSize: 256,
          maxZoom: 18,
        })

    const mapControl = defaultControls({
      zoom: false, // hides + / – zoom buttons
      attribution: false, // hides the “i” attribution button
      rotate: false // hides the rotate-to-north button (if present)
    }).extend([new GoogleLogoControl()])

    const new_map = new OlMap({
      layers: [googleMapLayer, baseXyzLayer, baseCogLayer],
      controls: mapControl,
      view: sharedView,
      ...options,
    })

    setTimeout(() => {
      const view = new_map.getView()
      view.animate({
        zoom: 15, // target zoom level
        duration: 500, // animation time in ms (1.5 seconds)
      })
    }, 500) // 3000ms = 3 seconds
    map.value = new_map

    // Attach tile loading listeners
    bindLoadingEvents(map.value)

    isInitialized.value = true
    return map.value
  }

  function labelTextDrawStyle (feature: FeatureLike, resolution?: number): Style | Style[] {
    const labelText = feature.get('labelText') as string | undefined

    const styles: Style[] = [
      new Style({
        fill: new Fill({
          color: 'rgba(255, 204, 51, 0.25)',
        }),
        stroke: new Stroke({
          color: '#ffcc33',
          width: 2,
        }),
      }),
    ]

    if (labelText) {
      styles.push(
        new Style({
          text: new Text({
            text: labelText,
            font: '16px "Segoe UI", Roboto, sans-serif',
            fill: new Fill({ color: '#1a1a1a' }),
            stroke: new Stroke({
              color: '#ffffff',
              width: 8,
            }),
            backgroundFill: new Fill({
              color: 'rgba(255, 255, 255, 0.25)',
            }),
            textAlign: 'left',
            textBaseline: 'middle',
            overflow: true,
          }),
        })
      )
    }

    return styles
  }

  function bindLoadingEvents (olMap: OlMap) {
    // Count tiles that start loading
    olMap.on('tileloadstart', () => {
      tilesLoading.value++
    })

    // Count tiles that finished (success or error)
    const onTileEnd = () => {
      tilesLoading.value = Math.max(0, tilesLoading.value - 1)
      tilesLoaded.value++
    }

    olMap.on('tileloadend', onTileEnd)
    olMap.on('tileloaderror', onTileEnd)

    // Optional: reset counters when a new load cycle starts
    olMap.on('loadstart', () => {
      // You can reset here if you want progress to restart on every pan/zoom
      // tilesLoaded.value = 0
      // tilesLoading.value = 0
    })

    olMap.on('loadend', () => {
      // All currently requested tiles are done
      // tilesLoading.value will already be 0
    })
  }

  /** Manually reset progress counters */
  function resetLoadingProgress () {
    tilesLoading.value = 0
    tilesLoaded.value = 0
  }

  function attachTo (target: HTMLElement | string | undefined) {
    if (!map.value) initMap()
    map.value!.setTarget(target)
    // force a size update after the container is visible
    map.value!.updateSize()
  }

  function setMapTo (target: OlMap) {
    map.value = target
  }

  /**
   * Detach without destroying the instance (important when leaving a page).
   */
  function detach () {
    map.value?.setTarget(undefined)
  }

  onUnmounted(() => {

  })

  return {
    map,
    isInitialized,

    // style
    labelTextDrawStyle,

    // loading
    isLoading,
    loadingProgress, // 0 ~ 100
    tilesLoading,
    tilesLoaded,
    resetLoadingProgress,

    // map control
    initMap,
    attachTo,
    setMapTo,
    detach,
  }
}
